import { ResetPasswordForm } from '@Components/Form/ResetPasswordForm'
import { Card, Dialog, Modal, RaisedButton, SkeletonBox } from '@eduact/ed-components'
import { FlexLayout, Typography } from '@eduact/ed-system'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GrLanguage } from 'react-icons/gr'
import { useAppDispatch } from '@Hooks/ReduxHooks'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useTheme } from '@eduact/student-theme'
import { logoutRequestAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { Icon as AbwIcon, SegmentedTabBar } from '@Components/AbwaabPlus'
import routes from '@Constants/Routes'
import UploadProfilePhoto from '../DashboardLayout/UploadProfilePhoto'
import { useGetUserProfileQuery } from '../DashboardSlice/Dashboard.slice'
import { ProfileBasicInfo } from './ProfileBasicInfo'
import { ProfileEducationalInfo } from './ProfileEducationalInfo'
import { ProfileInfoPanel } from './ProfileInfoPanel'
import { PanelInfo, ProfileInfoPanelProvider } from './ProfileInfoPanelContext'
import { ProfilePersonalInfo } from './ProfilePersonalInfo'
import { ProfileTabsProvider } from './ProfileTabsProvider'

/**
 * حسابي — the design's account page: avatar header (photo upload kept) with
 * education chips, بطاقة الطالب panel (view/download unchanged), the three
 * profile forms behind a SegmentedTabBar (أساسية/تعليمية/أخرى — same RHF
 * forms, dirty-state guard and unsaved-changes dialog preserved; forms stay
 * mounted so switching never loses input), and the design's links card
 * (الأجهزة المسجّلة / الأسئلة الشائعة / تسجيل الخروج).
 */
const Profile = () => {
	const [active, setActive] = useState('0')
	const [t, i18n] = useTranslation('studentDashboard')
	const { t: tEdu } = useTranslation('educational_info')
	const { data, isLoading } = useGetUserProfileQuery()
	const [panelInfo, setPanelInfo] = useState<PanelInfo>('card')
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { setTheme, theme } = useTheme()

	// Same switch as the header's — this row is the mobile doorway (the authed
	// mobile top bar has no language icon).
	const changeLanguage = () => {
		i18n.changeLanguage(i18n.language === 'ar-EG' ? 'en-GB' : 'ar-EG')
		if (i18n.language === 'ar-EG') {
			setTheme?.({ ...theme, direction: 'rtl' })
		} else {
			setTheme?.({ ...theme, direction: 'ltr' })
		}
	}

	// dirtiness per tab — all three forms stay mounted, so a hidden form's
	// dirty state can still block switches from a clean tab
	const [dirtyTabs, setDirtyTabs] = useState<Record<string, boolean>>({})
	const setTabDirty = (tab: string) => (dirty: boolean) => setDirtyTabs(prev => (prev[tab] === dirty ? prev : { ...prev, [tab]: dirty }))
	const [blockedTabValue, setBlockTabValue] = useState<string | undefined>()
	const [openWarningDialog, setOpenWarningDialog] = useState(false)
	const [navigatedBlockedTab, setNavigatedBlockedTab] = useState(false)
	const [openPasswordModal, setOpenPasswordModal] = useState(false)

	const handleCloseWarning = () => {
		setBlockTabValue(undefined)
		setOpenWarningDialog(false)
	}
	const handleConfirmWarning = () => {
		setDirtyTabs(prev => ({ ...prev, [active]: false }))
		blockedTabValue && setActive(blockedTabValue)
		setNavigatedBlockedTab(true)
		setOpenWarningDialog(false)
	}
	const handleTabChange = (value: string) => {
		setPanelInfo('card')
		if (!dirtyTabs[active]) {
			setActive(value)
			return
		}
		setBlockTabValue(value)
		setOpenWarningDialog(true)
		setNavigatedBlockedTab(false)
	}

	const handleLogout = async () => {
		// Awaited: the navigation below would otherwise cancel POST /auth/logout.
		await dispatch(logoutRequestAction())
		window.location.assign('/')
	}

	const tabLabels = [
		{ value: '0', label: t('tab_basic', { defaultValue: 'أساسية' }) },
		{ value: '1', label: t('tab_edu', { defaultValue: 'تعليمية' }) },
		{ value: '2', label: t('tab_other', { defaultValue: 'أخرى' }) },
	]

	const profile = data?.data
	const chips: { label: string; tone: 'blue' | 'gray' }[] = []
	if (profile?.student?.educationType?.name)
		chips.push({ label: tEdu(`educational_type.${profile.student.educationType.name}`, { defaultValue: profile.student.educationType.name }), tone: 'blue' })
	if (profile?.student?.educationYear?.name)
		chips.push({ label: tEdu(`academic_year.${profile.student.educationYear.name}`, { defaultValue: profile.student.educationYear.name }), tone: 'blue' })
	if (profile?.student?.educationSection?.name)
		chips.push({ label: tEdu(`educational_field.${profile.student.educationSection.name}`, { defaultValue: profile.student.educationSection.name }), tone: 'gray' })
	if (profile?.student?.educationLanguage?.name)
		chips.push({ label: tEdu(`academic_lang.${profile.student.educationLanguage.name}`, { defaultValue: profile.student.educationLanguage.name }), tone: 'gray' })

	return (
		<Page>
			<Dialog
				center
				padding={{ sm: '1rem', lg: '2rem' }}
				width={{ lg: '20vw' }}
				height={'fit-content'}
				open={openWarningDialog}
				persistent
				onClose={handleCloseWarning}
			>
				<FlexLayout flexDirection={'column'} gridGap="2rem">
					<Typography fontWeight={600}>{t('unsaved_changes')}</Typography>
					<FlexLayout flexWrap={'wrap'} flexDirection={{ sm: 'column', md: 'row' }} justifyContent={'flex-end'} gridGap="1rem" width="100%">
						<RaisedButton onClick={handleCloseWarning} variant="princetonOrange" outlined btnSize={'small'}>
							{t('stay_on_page')}
						</RaisedButton>
						<RaisedButton onClick={handleConfirmWarning} btnSize={'small'}>
							{t('leave_page')}
						</RaisedButton>
					</FlexLayout>
				</FlexLayout>
			</Dialog>

			<SkeletonBox borderRadius={'16px'} height={{ sm: '80vh', lg: '70vh' }} width={'100%'} isLoading={isLoading}>
				<ProfileInfoPanelProvider
					currentInfo={panelInfo}
					setCurrentInfo={setPanelInfo}
					openPasswordModal={openPasswordModal}
					setPasswordModal={setOpenPasswordModal}
				>
					<Modal
						width={'fit-content'}
						height="fit-content"
						onClose={() => {
							setPanelInfo('card')
							setOpenPasswordModal(false)
						}}
						withBackdrop
						withStyling
						center
						open={openPasswordModal}
					>
						<Card width={'90vw'} padding={'2rem'} variant={'light'}>
							<ResetPasswordForm />
						</Card>
					</Modal>

					<Columns>
						{/* Side column: avatar header + student ID card panel */}
						<Side>
							<AvatarHeader>
								<UploadProfilePhoto />
								<div className="name">
									{profile?.first_name} {profile?.last_name}
								</div>
								{chips.length > 0 && (
									<div className="chips">
										{chips.map(c => (
											<Chip key={c.label} data-tone={c.tone}>
												{c.label}
											</Chip>
										))}
									</div>
								)}
							</AvatarHeader>
							<ProfileInfoPanel />
						</Side>

						{/* Main column: tabbed forms + links */}
						<Main>
							<SegmentedTabBar
								tabs={tabLabels.map(tl => tl.label)}
								value={tabLabels.find(tl => tl.value === active)?.label ?? tabLabels[0].label}
								onChange={label => {
									const target = tabLabels.find(tl => tl.label === label)
									if (target) handleTabChange(target.value)
								}}
							/>

							{data && (
								<>
									<Pane hidden={active !== '0'}>
										<FormCard>
											<ProfileTabsProvider isDirty={!!dirtyTabs['0']} blockedNavigationDone={navigatedBlockedTab} setIsDirty={setTabDirty('0')}>
												<ProfileBasicInfo />
											</ProfileTabsProvider>
										</FormCard>
									</Pane>
									<Pane hidden={active !== '1'}>
										<FormCard>
											<ProfileTabsProvider isDirty={!!dirtyTabs['1']} blockedNavigationDone={navigatedBlockedTab} setIsDirty={setTabDirty('1')}>
												<ProfileEducationalInfo />
											</ProfileTabsProvider>
										</FormCard>
									</Pane>
									<Pane hidden={active !== '2'}>
										<FormCard>
											<ProfileTabsProvider isDirty={!!dirtyTabs['2']} blockedNavigationDone={navigatedBlockedTab} setIsDirty={setTabDirty('2')}>
												<ProfilePersonalInfo />
											</ProfileTabsProvider>
										</FormCard>
									</Pane>
								</>
							)}

							{/* Links card */}
							<LinksCard>
								<LinkRow type="button" onClick={() => navigate(routes.accountDevices)}>
									<span className="tile" data-tone="blue">
										<AbwIcon name="layout-grid" size={16} />
									</span>
									<span className="label">{t('link_devices', { defaultValue: 'الأجهزة المسجّلة' })}</span>
									<span className="chev">
										<AbwIcon name="chevron-left" size={16} />
									</span>
								</LinkRow>
								<LinkRow type="button" onClick={() => navigate('/dashboard')}>
									<span className="tile" data-tone="blue">
										<AbwIcon name="trending-up" size={16} />
									</span>
									<span className="label">{t('link_dashboard', { defaultValue: 'لوحة المتابعة' })}</span>
									<span className="chev">
										<AbwIcon name="chevron-left" size={16} />
									</span>
								</LinkRow>
								<LinkRow type="button" onClick={changeLanguage}>
									<span className="tile" data-tone="blue">
										<GrLanguage size={16} />
									</span>
									<span className="label">{t('link_language', { defaultValue: 'اللغة' })}</span>
									<span className="value">{i18n.language === 'ar-EG' ? 'English' : 'عربي'}</span>
								</LinkRow>
								<LinkRow type="button" onClick={() => navigate('/faqs')}>
									<span className="tile" data-tone="blue">
										<AbwIcon name="help-circle" size={16} />
									</span>
									<span className="label">{t('link_faqs', { defaultValue: 'الأسئلة الشائعة' })}</span>
									<span className="chev">
										<AbwIcon name="chevron-left" size={16} />
									</span>
								</LinkRow>
								<LinkRow type="button" data-danger="true" onClick={handleLogout}>
									<span className="tile" data-tone="error">
										<AbwIcon name="log-out" size={16} />
									</span>
									<span className="label">{t('link_logout', { defaultValue: 'تسجيل الخروج' })}</span>
									<span className="chev">
										<AbwIcon name="chevron-left" size={16} />
									</span>
								</LinkRow>
							</LinksCard>
						</Main>
					</Columns>
				</ProfileInfoPanelProvider>
			</SkeletonBox>
		</Page>
	)
}

export default Profile

/* Design shell: 512px centred column on mobile, 960px on desktop. */
const Page = styled.div`
	width: 100%;
	max-width: 512px;
	margin: 0 auto;
	background: var(--surface-page);
	padding: 16px 16px 40px;
	@media (min-width: 1024px) {
		max-width: 960px;
		padding: 16px 24px 48px;
	}
`
/* Mobile: one column. Desktop: 360px side (avatar + ID card) + fluid main. */
const Columns = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	align-items: stretch;
	@media (min-width: 1024px) {
		flex-direction: row;
		align-items: flex-start;
	}
`
const Side = styled.div`
	width: 100%;
	flex: none;
	display: flex;
	flex-direction: column;
	gap: 12px;
	@media (min-width: 1024px) {
		width: 360px;
	}
`
const Main = styled.div`
	flex: 1;
	min-width: 0;
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 12px;
`
const AvatarHeader = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 8px 0;
	.name {
		font: var(--fw-bold) 18px var(--font-sans);
		color: var(--text-primary);
	}
	.chips {
		display: flex;
		gap: 6px;
		flex-wrap: wrap;
		justify-content: center;
	}
`
const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	height: 24px;
	padding: 0 10px;
	border-radius: var(--r-full);
	font: var(--fw-bold) 11px var(--font-sans);
	&[data-tone='blue'] {
		background: var(--blue-10);
		color: var(--blue);
	}
	&[data-tone='gray'] {
		background: var(--surface-sunken-strong);
		color: var(--text-secondary);
	}
`
const Pane = styled.div`
	&[hidden] {
		display: none;
	}
`
const FormCard = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: 16px;
	box-shadow: var(--sh-rest);
	padding: 16px;
`
const LinksCard = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: 16px;
	box-shadow: var(--sh-rest);
	overflow: hidden;
`
const LinkRow = styled.button`
	width: 100%;
	border: none;
	background: none;
	padding: 14px 16px;
	border-bottom: 1px solid var(--border);
	display: flex;
	align-items: center;
	gap: 12px;
	cursor: pointer;
	font-family: var(--font-sans);
	&:last-child {
		border-bottom: none;
	}
	.tile {
		flex: none;
		width: 36px;
		height: 36px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		&[data-tone='blue'] {
			background: var(--blue-10);
			color: var(--blue);
		}
		&[data-tone='error'] {
			background: var(--error-10);
			color: var(--error-dark);
		}
	}
	.label {
		flex: 1;
		text-align: start;
		font: var(--fw-medium) 14px var(--font-sans);
		color: var(--text-primary);
	}
	&[data-danger='true'] .label {
		font-weight: var(--fw-bold);
		color: var(--error-dark);
	}
	.chev {
		color: var(--text-disabled);
		display: flex;
		html:not([dir='rtl']) & {
			transform: scaleX(-1);
		}
	}
	.value {
		font: var(--fw-bold) 13px var(--font-sans);
		color: var(--blue);
	}
`
